import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { preferencesService, type Theme } from '../services/preferences.service';

type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'atlas-theme';

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') {
      return v;
    }
  } catch {
    /* ignore */
  }
  return 'system';
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return theme;
}

function applyToDOM(theme: ResolvedTheme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Quando true, sincroniza com /me/preferences. Habilitar após autenticação. */
  syncWithBackend?: boolean;
}

export function ThemeProvider({ children, syncWithBackend = false }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolve(readStoredTheme())
  );

  useEffect(() => {
    const r = resolve(theme);
    setResolvedTheme(r);
    applyToDOM(r);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') {
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const r: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolvedTheme(r);
      applyToDOM(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  useEffect(() => {
    if (!syncWithBackend) {
      return;
    }
    let cancelled = false;
    preferencesService
      .get()
      .then((prefs) => {
        if (!cancelled) {
          setThemeState(prefs.theme);
        }
      })
      .catch(() => {
        /* manter local */
      });
    return () => {
      cancelled = true;
    };
  }, [syncWithBackend]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      if (syncWithBackend) {
        preferencesService.update({ theme: next }).catch(() => {
          /* não-fatal — local persiste */
        });
      }
    },
    [syncWithBackend]
  );

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
